"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useState } from "react";
import { cleanProps, cn } from "../../../utils/index.js";
import { CdnIcon } from "../../CdnIcon/index.js";
import { HorizonIcon } from "../../HorizonIcon/index.js";
import { Image } from "../../Image/index.js";
import { Span } from "../../Text/Span.js";
import { filterChipVariants } from "../FilterChip.variants.js";
const NonOpenableFilterChip_NonOpenableFilterChip = /*#__PURE__*/ forwardRef(function({ label, className, onClick, selected = false, onSelectionChange, iconLeft, customIcon, count, id, name, value, helperText, showCheckIcon = true, filterType = 'checkbox', customNode, favicon, faviconAlt, labelImage, labelImageAlt, isChipRange = false, ...props }, ref) {
    const [optimisticSelected, setOptimisticSelected] = useState(null);
    const selectedState = optimisticSelected ?? selected;
    const isControlled = void 0 !== onSelectionChange;
    const handleClick = (event)=>{
        if ('radio' === filterType && selectedState) return void onClick?.(event, true);
        if (isControlled) onSelectionChange(!selectedState);
        else setOptimisticSelected(!selectedState);
        onClick?.(event, !selectedState);
    };
    useEffect(()=>{
        setOptimisticSelected(null);
    }, [
        selected
    ]);
    const { labelTextSlot, helperTextSlot, containerSlot, iconLeftSlot, checkIconSlot, iconRightSlot, countSlot, checkboxSlot, faviconSlot, faviconImg, labelImageSlot, chipRangeSlot } = filterChipVariants({
        ...props,
        disabled: props.disabled,
        hasIconLeft: Boolean(iconLeft ?? customIcon),
        selectedState,
        hasCount: void 0 !== count && count >= 0,
        showCheckIcon,
        isChipRange
    });
    const classes = cn(containerSlot(), className);
    const iconSize = 'tile' === props.type ? '20' : '18';
    const ariaPressed = selectedState;
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsxs("button", {
                ref: ref,
                className: classes,
                onClick: handleClick,
                disabled: props.disabled,
                "aria-pressed": ariaPressed,
                "aria-label": label || void 0,
                ...cleanProps(props, [
                    'selected',
                    'type',
                    'elevated',
                    'emphasis',
                    'opens'
                ]),
                children: [
                    showCheckIcon && /*#__PURE__*/ jsx(CdnIcon, {
                        className: cn(checkIconSlot()),
                        iconName: 'check',
                        size: iconSize
                    }),
                    customNode ? /*#__PURE__*/ jsx("div", {
                        className: "flex w-full",
                        children: customNode
                    }) : /*#__PURE__*/ jsxs(Fragment, {
                        children: [
                            iconLeft && !favicon && !customIcon && /*#__PURE__*/ jsx(CdnIcon, {
                                className: cn(iconLeftSlot()),
                                iconName: iconLeft,
                                size: iconSize
                            }),
                            customIcon && /*#__PURE__*/ jsx(HorizonIcon, {
                                className: cn(iconLeftSlot()),
                                name: customIcon,
                                size: iconSize
                            }),
                            favicon && /*#__PURE__*/ jsx(Span, {
                                className: faviconSlot(),
                                children: /*#__PURE__*/ jsx(Image, {
                                    src: favicon,
                                    alt: faviconAlt || '',
                                    className: faviconImg()
                                })
                            }),
                            /*#__PURE__*/ jsxs("div", {
                                className: cn('flex flex-col', chipRangeSlot()),
                                children: [
                                    /*#__PURE__*/ jsx("span", {
                                        className: labelTextSlot(),
                                        children: labelImage ? /*#__PURE__*/ jsx(Image, {
                                            src: labelImage,
                                            alt: labelImageAlt || '',
                                            className: labelImageSlot()
                                        }) : label
                                    }),
                                    'tile' === props.type && helperText && /*#__PURE__*/ jsx("span", {
                                        className: helperTextSlot(),
                                        children: helperText
                                    })
                                ]
                            }),
                            /*#__PURE__*/ jsx("span", {
                                className: countSlot(),
                                children: void 0 !== count && count >= 0 ? `(${count})` : null
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: 'hidden [.openable-filter-chip_&]:inline-flex',
                        children: /*#__PURE__*/ jsx(CdnIcon, {
                            className: iconRightSlot(),
                            iconName: 'arrow_drop_down',
                            size: iconSize
                        })
                    })
                ]
            }),
            /*#__PURE__*/ jsx("input", {
                className: checkboxSlot(),
                type: filterType,
                role: filterType,
                id: id,
                name: name,
                "data-testid": id,
                disabled: props.disabled,
                checked: selectedState,
                onChange: handleClick,
                value: value,
                tabIndex: -1,
                "aria-label": label || void 0
            })
        ]
    });
});
NonOpenableFilterChip_NonOpenableFilterChip.displayName = 'NonOpenableFilterChip';
export { NonOpenableFilterChip_NonOpenableFilterChip as NonOpenableFilterChip };
